import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLanguage } from "../i18n";
import { Reveal, MaskedLine, Label } from "../components/Motion";
import { usePageMeta } from "../lib/seo";

const IMAGES = {
  custom: ["/images/services-custom.webp"],
  overhaul: ["/images/service-overhaul.webp", "/images/services-overhaul.webp", "/images/overhaul-excavator.webp", "/images/overhaul-agri.webp"],
};

const DEPT_ROW_IMAGES = [
  "https://images.pexels.com/photos/19895882/pexels-photo-19895882.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ["/images/stairs.webp", "/images/dept-fabrication.webp"],
  "/images/dept-assembly.webp",
  "/images/dept-milling.webp",
  "/images/dept-turning.webp",
  "/images/dept-cnc.webp",
];

export default function Services() {
  const { t } = useLanguage();
  usePageMeta(t("meta.services.t"), t("meta.services.d"));

  const coreServices = [
    {
      id: "custom",
      imgs: IMAGES.custom,
      title: t("servicesPage.customTitle"),
      body: t("servicesPage.customBody"),
      points: t("servicesPage.customPoints"),
    },
    {
      id: "overhaul",
      imgs: IMAGES.overhaul,
      title: t("servicesPage.overhaulTitle"),
      body: t("servicesPage.overhaulBody"),
      points: t("servicesPage.overhaulPoints"),
    },
  ];

  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section data-testid="services-header" className="mx-auto max-w-7xl px-6 pb-16 pt-40 md:px-12 md:pb-24 md:pt-56">
        <MaskedLine delay={0.1}>
          <p className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
            <span className="inline-block h-px w-12 bg-brand-yellow" />
            GTECH SRL — {t("nav.services")}
          </p>
        </MaskedLine>
        <h1 data-testid="services-title" className="font-display text-[clamp(2.8rem,9vw,8rem)] font-black leading-[0.95] tracking-tighter">
          <MaskedLine delay={0.25}>
            {t("servicesPage.title")}<span className="text-brand-yellow">{t("servicesPage.titleAccent")}</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p data-testid="services-sub" className="mt-8 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">
            {t("servicesPage.sub")}
          </p>
        </Reveal>
      </section>

      {/* CORE SERVICES */}
      <section data-testid="core-services-section" className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <Reveal><Label>{t("servicesPage.coreLabel")}</Label></Reveal>
          <div className="mt-16 space-y-24">
            {coreServices.map((s, i) => (
              <div
                key={s.id}
                data-testid={`core-service-${s.id}`}
                className={`grid items-center gap-10 md:grid-cols-2 ${i % 2 === 1 ? "" : ""}`}
              >
                <Reveal className={i % 2 === 1 ? "md:order-2" : ""}>
                  <div className={`grid gap-3 ${s.imgs.length > 1 ? "grid-cols-2" : ""}`}>
                    {s.imgs.map((src, j) => (
                      <div key={j} className="group relative overflow-hidden border border-brand-line">
                        <img
                          src={src}
                          alt={s.title}
                          loading="lazy"
                          className="aspect-[4/3] w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>
                    ))}
                  </div>
                </Reveal>
                <div className={i % 2 === 1 ? "md:order-1" : ""}>
                  <Reveal>
                    <h2 className="font-display text-3xl font-bold tracking-tight md:text-5xl">{s.title}</h2>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <p className="mt-6 text-base leading-relaxed text-neutral-400 md:text-lg">{s.body}</p>
                  </Reveal>
                  <Reveal delay={0.2}>
                    <ul className="mt-8 space-y-3">
                      {s.points.map((p, j) => (
                        <li key={j} className="flex items-start gap-3 text-sm text-neutral-300 md:text-base">
                          <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-yellow" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                  <Reveal delay={0.3}>
                    <Link
                      to="/contact"
                      data-testid={`core-service-cta-${s.id}`}
                      className="group mt-10 inline-flex items-center gap-2 bg-brand-yellow px-7 py-4 text-sm font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300"
                    >
                      {t("servicesPage.cta")}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Reveal>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEPARTMENTS LIST */}
      <section data-testid="departments-list-section" className="border-t border-white/10 bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <Reveal>
            <h2 data-testid="departments-list-title" className="font-display text-4xl font-bold tracking-tight md:text-6xl">
              {t("servicesPage.deptTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-base text-neutral-400">{t("servicesPage.deptNote")}</p>
          </Reveal>
          <div className="mt-16">
            {t("departments.items").map((d, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <div
                  data-testid={`dept-row-${i}`}
                  className="group grid gap-4 border-t border-white/10 py-8 transition-colors duration-500 last:border-b hover:bg-white/[0.02] md:grid-cols-12 md:items-center md:gap-6 md:px-4"
                >
                  <span className="font-display text-xl font-black text-brand-yellow md:col-span-1">0{i + 1}</span>
                  <h3 className="font-display text-2xl font-bold tracking-tight transition-colors duration-300 group-hover:text-brand-yellow md:col-span-3 md:text-3xl">
                    {d.name}
                  </h3>
                  <p className="text-sm leading-relaxed text-neutral-400 md:col-span-5 md:text-base">{d.desc}</p>
                  <div className={`grid gap-3 md:col-span-3 ${Array.isArray(DEPT_ROW_IMAGES[i]) ? "grid-cols-2" : ""}`}>
                    {(Array.isArray(DEPT_ROW_IMAGES[i]) ? DEPT_ROW_IMAGES[i] : [DEPT_ROW_IMAGES[i]]).map((src, j) => (
                      <div key={j} className="overflow-hidden border border-brand-line">
                        <img
                          src={src}
                          alt={d.name}
                          loading="lazy"
                          className="h-36 w-full object-cover grayscale transition-all duration-700 hover:scale-105 hover:grayscale-0 md:h-24"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="process-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <Reveal><Label>{t("servicesPage.processLabel")}</Label></Reveal>
        <Reveal delay={0.1}>
          <h2 data-testid="process-title" className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl">
            {t("servicesPage.processTitle")}
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px bg-brand-line md:grid-cols-5">
          {t("servicesPage.steps").map((s, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <div data-testid={`process-step-${i}`} className="group h-full bg-brand-black p-6 transition-colors duration-500 hover:bg-brand-surface md:p-8">
                <span className="font-display text-4xl font-black text-white/10 transition-colors duration-500 group-hover:text-brand-yellow/40">
                  0{i + 1}
                </span>
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight">{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="services-cta-section" className="mx-auto max-w-7xl px-6 py-24 text-center md:px-12 md:py-32">
        <Reveal>
          <h2 className="font-display text-4xl font-black tracking-tighter md:text-6xl">
            {t("ctaSection.title").split(" ")[0]}{" "}
            <span className="text-brand-yellow">{t("ctaSection.title").split(" ").slice(1).join(" ")}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-6 max-w-xl text-base text-neutral-400 md:text-lg">{t("ctaSection.sub")}</p>
        </Reveal>
        <Reveal delay={0.25} className="mt-10">
          <Link
            to="/contact"
            data-testid="services-cta-button"
            className="group inline-flex items-center gap-3 bg-brand-yellow px-10 py-5 font-display text-base font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300"
          >
            {t("ctaSection.btn")}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
